/** Evita `NaN` em inputs `type="number"` vazios antes da validação Zod. */
export function numberInputValueAs(value: unknown): number | undefined {
  if (value === '' || value === null || value === undefined) return undefined;
  const num = typeof value === 'number' ? value : Number(value);
  return Number.isNaN(num) ? undefined : num;
}

/** Converte mensagens padrão do Zod (inglês) para PT-BR no feedback do formulário. */
export function translateZodFieldMessage(message?: string): string | undefined {
  if (!message) return undefined;
  const lower = message.toLowerCase();
  if (lower.includes('expected number') && lower.includes('nan')) {
    return 'Informe um número válido';
  }
  if (lower.includes('expected string') && lower.includes('nan')) {
    return 'Informe um valor válido';
  }
  if (lower.includes('invalid input')) {
    return 'Valor inválido';
  }
  if (lower.includes('expected object') && lower.includes('null')) {
    return 'Selecione um valor';
  }
  return message;
}

const FIELD_ERROR_META_KEYS = new Set(['ref', 'type', 'types', 'root', 'message']);

function isLeafFieldError(value: unknown): value is { message: string } {
  return !!value && typeof value === 'object' && typeof (value as { message?: unknown }).message === 'string';
}

/** Primeiro erro de validação com caminho (ex.: `defensor.funcao`). */
export function firstFieldError(
  errors: unknown,
  prefix = '',
): { path: string; message: string } | undefined {
  if (!errors || typeof errors !== 'object') return undefined;

  if (isLeafFieldError(errors) && prefix) {
    return { path: prefix, message: errors.message };
  }

  for (const [key, value] of Object.entries(errors as Record<string, unknown>)) {
    if (FIELD_ERROR_META_KEYS.has(key)) continue;
    const next = prefix ? `${prefix}.${key}` : key;
    const found = firstFieldError(value, next);
    if (found) return found;
  }

  return undefined;
}

export function describeFormValidationError(
  errors: unknown,
  fieldLabels: Record<string, string> = {},
  fallback = 'Corrija os campos destacados antes de salvar.',
): string {
  const first = firstFieldError(errors);
  if (!first) return fallback;

  const pathWithoutId = first.path.replace(/\.id$/, '');
  const label = fieldLabels[first.path] ?? fieldLabels[pathWithoutId];
  const translated = translateZodFieldMessage(first.message) ?? first.message;
  const isGenericZod = /expected |invalid_type|invalid type|received |selecione um valor/i.test(translated);

  if (label && isGenericZod) {
    return `Corrija o campo "${label}" antes de salvar.`;
  }
  if (translated && !isGenericZod) {
    return label ? `${label}: ${translated}` : translated;
  }
  if (label) {
    return `Corrija o campo "${label}" antes de salvar.`;
  }
  return fallback;
}
